import React, { useState, useEffect, useCallback } from 'react';
import ImageWithLoader from './ImageWithLoader';

const getInitials = (name: string): string => {
  if (!name) return '?';
  const trimmedName = name.trim();
  if (!trimmedName) return '?';

  const names = trimmedName.split(' ').filter(Boolean);
  if (names.length === 1) return names[0].slice(0, 2).toUpperCase();

  const initials = names.map(n => n[0]).join('');
  return initials.slice(0, 2).toUpperCase();
};

const COLORS = [
  'bg-action',       // ember — warm, brand
  'bg-status-info',  // seafoam — cool, calm
  'bg-status-ok',    // sage — earthy, grounded
  'bg-chip-photo',   // steel blue — neutral, professional
  'bg-status-warn',  // blush — warm coral, distinct from ember
];

const getColorForString = (str: string): string => {
  if (!str) return COLORS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash % COLORS.length);
  return COLORS[index];
};

interface AvatarProps {
  name: string;
  avatarUrl?: string | null;
  id: string;
  className?: string;
}

const Avatar: React.FC<AvatarProps> = ({ name, avatarUrl, id, className = 'w-10 h-10' }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [avatarUrl]);

  const handleImageError = useCallback(() => {
    setHasError(true);
  }, []);

  if (avatarUrl && !hasError) {
    return (
      <ImageWithLoader
        src={avatarUrl}
        alt={name}
        title={name}
        className={className}
        onError={handleImageError}
      />
    );
  }

  const initials = getInitials(name);
  const bgColor = getColorForString(id);

  // Determine font size based on className width
  let fontSize = 'text-[1.1rem]';
  if (className.includes('w-6') || className.includes('w-5')) {
    fontSize = 'text-[0.65rem]';
  } else if (className.includes('w-8') || className.includes('w-7')) {
    fontSize = 'text-xs';
  }

  return (
    <div
      className={`${className} ${bgColor} flex items-center justify-center text-white font-bold overflow-hidden`}
      title={name}
    >
      <span className={`${fontSize} leading-none tracking-tighter`}>{initials}</span>
    </div>
  );
};

export default Avatar;